import { Link as DemoLink } from 'react-router-dom';
import {
  DEMO_WORKBOOK_ID,
  DEMO_MAP_PATH,
  DEMO_PRACTICE_PATH,
} from '@/pages/try/studyExperience/demoLearningOutcomes';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { ApiError } from '@/lib/api';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMe } from '@/auth/useAuth';
import { StudyShell } from './StudyShell';
import { StudyNotesBatch } from './StudyNotesBatch';
import { StudyNoteEditor } from './StudyNoteEditor';
import { getStudyHome, STUDY_HOME_QUERY_KEY } from './studyApi';
import { listStudyNotes, readStudyNote } from './studyNotesApi';

/**
 * Where "Back to my review plan" may go: Study Home (the task panel lives there since the plan
 * page retired, My year PRD §4), a subject page, or an old plan link. Nothing outside Study.
 */
const RETURN_PATH = /^\/learn\/study(?:\/plan|\/subjects\/[a-z0-9_-]+)?(?:\?[^#]*)?$/;

export function StudyNotesPage() {
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const owner = me.data?.kind === 'kid' ? me.data.sub : null;
  const noteId = params.get('note');
  const returnParam = params.get('return') ?? '';
  const returnTo = RETURN_PATH.test(returnParam) ? returnParam : null;
  const editing = Boolean(noteId || params.get('new'));
  const search = params.get('q') ?? '';
  const term = params.get('term') ?? '';
  const subject = params.get('subject') ?? '';
  const topic = params.get('topic') ?? '';
  const taskId = params.get('task') ?? '';
  const status = params.get('status') === 'archived' ? 'archived' : 'active';
  const setFilter = (key: string, value: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key === 'term') next.delete('subject');
        return next;
      },
      { replace: true },
    );
  const openNote = (id?: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('note');
      next.delete('new');
      if (id === 'new') next.set('new', '1');
      else if (id) next.set('note', id);
      return next;
    });
  const [createdId, setCreatedId] = useState<string | null>(null);
  const home = useQuery({
    queryKey: [...STUDY_HOME_QUERY_KEY, owner],
    queryFn: getStudyHome,
    enabled: Boolean(owner),
    retry: false,
  });
  const notes = useInfiniteQuery({
    queryKey: ['study-notes', owner, search, term, subject, topic, taskId, status],
    queryFn: ({ pageParam }) =>
      listStudyNotes({
        search,
        term_id: term,
        subject_key: subject,
        topic,
        task_id: taskId,
        status,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: Boolean(owner) && !editing,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['study-note', owner, noteId],
    queryFn: () => readStudyNote(noteId!),
    enabled: Boolean(owner && noteId),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const accessRejected =
    detail.isError &&
    detail.error instanceof ApiError &&
    [401, 403, 404].includes(detail.error.status);
  const terms =
    home.data?.academic_years?.flatMap((y) =>
      y.terms.map((t) => ({ ...t, label: `${y.school_year} · ${t.label}` })),
    ) ??
    home.data?.terms ??
    [];
  const subjects = [
    ...new Map(
      (term
        ? (terms.find((t) => t.id === term)?.subjects ?? [])
        : [...terms.flatMap((t) => t.subjects ?? []), ...(home.data?.subjects ?? [])]
      ).map((s) => [s.subject_key, s]),
    ).values(),
  ];
  return (
    <StudyShell>
      <Link
        className="text-sm font-bold text-slate2 underline"
        to={returnTo ?? '/learn/study/materials'}
      >
        {returnTo ? 'Back to my review plan' : 'All materials'}
      </Link>
      <header className="study-page-intro mt-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-brand-mint">
            {isLocalStudyExperience()
              ? 'Fictional demo · this tab only'
              : 'Private to me · phone and desktop'}
          </p>
          <h1 className="mt-2 text-3xl font-extrabold text-ink">Class notes</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-soft">
            {isLocalStudyExperience()
              ? 'Keep photos and your own words together. Changes stay in this demo tab until reload, reset or exit.'
              : 'Keep photos and your own words together. Open the same saved notes on another device.'}
          </p>
        </div>
        {!editing && (
          <button className="btn-pill-primary" onClick={() => openNote('new')}>
            Take a photo or add notes
          </button>
        )}
      </header>
      {isLocalStudyExperience() && editing && (
        <p className="my-4 flex flex-wrap gap-4 text-sm font-bold underline">
          <DemoLink to={`/learn/study/materials/${DEMO_WORKBOOK_ID}`}>
            Open the source workbook
          </DemoLink>
          <DemoLink to={DEMO_MAP_PATH}>Open the connected mind map</DemoLink>
          <DemoLink to={DEMO_PRACTICE_PATH}>Open prepared practice and solutions</DemoLink>
          <DemoLink to="/learn/study/review">Open Review</DemoLink>
        </p>
      )}
      {isLocalStudyExperience() && detail.data && !accessRejected && (
        <article
          aria-label="Saved note content"
          className="my-6 max-w-4xl rounded-3xl border border-hairline bg-canvas-pure p-5 text-ink"
        >
          <h2 className="text-2xl font-extrabold">{detail.data.title}</h2>
          <p className="mt-2 text-xs font-bold text-slate2">
            Saved fictional example · full content · edit below
          </p>
          <p className="mt-4 whitespace-pre-wrap leading-relaxed">{detail.data.body}</p>
        </article>
      )}
      {owner && editing && !noteId && (
        <StudyNoteEditor
          key={`${owner}:new`}
          home={home.data}
          owner={owner}
          onClose={() => openNote()}
          onCreated={(id) => {
            setCreatedId(id);
            openNote(id);
          }}
          initialTaskId={taskId || undefined}
          initialFiling={{
            term_id: params.get('term') ?? '',
            subject_key: params.get('subject') ?? '',
          }}
        />
      )}
      {owner && noteId && detail.isFetchedAfterMount && detail.data && !accessRejected && (
        <StudyNoteEditor
          key={`${owner}:${noteId}`}
          note={detail.data}
          home={home.data}
          owner={owner}
          onClose={() => openNote()}
          savedNotice={createdId === noteId}
        />
      )}
      {noteId && !detail.isFetchedAfterMount && !detail.isError && (
        <p className="mt-6" role="status">
          Opening your notes…
        </p>
      )}
      {editing && detail.isError && (
        <p className="mt-6 text-ink" role="alert">
          These notes could not be opened.{' '}
          <button className="underline" onClick={() => void detail.refetch()}>
            Try again
          </button>
        </p>
      )}
      {!editing && (
        <>
          {taskId && (
            <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl bg-wash-sky p-4 text-sm text-ink">
              <p>Notes linked to this task. You can use one note for several tasks.</p>
              <button className="btn-pill-ghost" onClick={() => setFilter('task', '')}>
                Show all notes
              </button>
            </div>
          )}
          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <label className="col-span-2 text-sm font-bold lg:col-span-1">
              Search notes
              <input
                className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
                type="search"
                maxLength={120}
                value={search}
                onChange={(e) => setFilter('q', e.target.value)}
                placeholder="Title, topic or your text"
              />
            </label>
            <label className="text-sm font-bold">
              Term
              <select
                aria-label="Term"
                className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
                value={term}
                onChange={(e) => {
                  setFilter('term', e.target.value);
                }}
              >
                <option value="">All Terms and years</option>
                <option value="inbox">Unfiled</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-bold">
              Subject
              <select
                aria-label="Subject"
                className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
                value={subject}
                onChange={(e) => setFilter('subject', e.target.value)}
              >
                <option value="">All subjects</option>
                {subjects.map((s) => (
                  <option key={s.subject_key} value={s.subject_key}>
                    {s.display_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-bold">
              Topic / chapter
              <input
                className="mt-1 min-h-11 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
                value={topic}
                maxLength={120}
                placeholder="Exact topic"
                onChange={(e) => setFilter('topic', e.target.value)}
              />
            </label>
            <label className="text-sm font-bold">
              Show
              <select
                className="mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3"
                value={status}
                onChange={(e) => setFilter('status', e.target.value)}
              >
                <option value="active">Current notes</option>
                <option value="archived">Archived notes</option>
              </select>
            </label>
          </div>
          {notes.isLoading && (
            <p className="mt-6" role="status">
              Loading notes…
            </p>
          )}
          {notes.isError && (
            <p className="mt-6" role="alert">
              Your notes could not be loaded.{' '}
              <button className="underline" onClick={() => void notes.refetch()}>
                Retry
              </button>
            </p>
          )}
          <StudyNotesBatch
            key={`${owner}:${search}:${term}:${subject}:${topic}:${taskId}:${status}`}
            owner={owner ?? ''}
            items={notes.data?.pages.flatMap((page) => page.items) ?? []}
            terms={terms.map((t) => ({ id: t.id, label: t.label }))}
            subjects={subjects}
            onOpen={openNote}
          />
          {notes.isSuccess && notes.data.pages.every((p) => p.items.length === 0) && (
            <p className="mt-6 rounded-3xl border border-dashed border-hairline p-6 text-ink-soft">
              No notes match this view. Start with a photo or your own words.
            </p>
          )}
          {notes.hasNextPage && (
            <button
              className="btn-pill-secondary mt-4"
              disabled={notes.isFetchingNextPage}
              onClick={() => void notes.fetchNextPage()}
            >
              Load more notes
            </button>
          )}
        </>
      )}
    </StudyShell>
  );
}
